import { Component, computed, input, output } from '@angular/core';
import { PipesModule } from 'app/shared/pipes';


export type AsyncItemType
    = 'text'
    | 'number'
    | 'number2'
    | 'meters'
    | 'currency0'
    | 'currency'
    | 'date'
    | 'areaImperial'
    | 'areaMetric'
    | 'link'
;

export type AsyncItemState = 'new' | 'loading' | 'done' | 'timeout' | 'error';

interface AsyncItemConfig {
    editable: boolean;
    deletable: boolean;
}


const DEFAULT_CONFIG: AsyncItemConfig = {
    editable: false,
    deletable: false,
};


export class AsyncItem {

    private readonly _config: AsyncItemConfig;

    constructor(
        readonly value: any,
        readonly state: AsyncItemState = 'new',
        readonly type: AsyncItemType = 'text',
        config: Partial<AsyncItemConfig> = DEFAULT_CONFIG,
        readonly deleted = false,
        readonly edited = false,
        private readonly _originalItem?: AsyncItem
    ) {
        if (typeof value === 'undefined') {
            this.value = null;
        }
        this._config = { ...DEFAULT_CONFIG, ...config };
    }

    get isValid() {
        return this.state === 'done' && typeof this.value !== 'undefined' && this.value !== null;
    }

    get isEditable() {
        return this._config.editable;
    }

    get isDeletable() {
        return this._config.deletable;
    }

    start() {
        return new AsyncItem(this.value, 'loading', this.type, this._config);
    }

    resolve(value: any): AsyncItem {
        if (this._originalItem && this._originalItem.state === 'loading') {
            return new AsyncItem(
                this.value,
                this.state,
                this.type,
                this._config,
                this.deleted,
                this.edited,
                this._originalItem.resolve(value)
            );
        }
        return new AsyncItem(value, 'done', this.type, this._config);
    }

    reject(reason = ''): AsyncItem {
        if (this._originalItem && this._originalItem.state === 'loading') {
            return new AsyncItem(
                this.value,
                this.state,
                this.type,
                this._config,
                this.deleted,
                this.edited,
                this._originalItem.reject(reason)
            );
        }
        return new AsyncItem(reason, 'error', this.type, this._config);
    }

    timeout(): AsyncItem {
        if (this._originalItem && this._originalItem.state === 'loading') {
            return new AsyncItem(
                this.value,
                this.state,
                this.type,
                this._config,
                this.deleted,
                this.edited,
                this._originalItem.timeout()
            );
        }
        return new AsyncItem(null, 'timeout', this.type, this._config);
    }

    delete() {
        return new AsyncItem(this.value, 'done', this.type, this._config, true, this.edited, this.edited ? this._originalItem : this);
    }

    edit(value: any) {
        return new AsyncItem(value, 'done', this.type, this._config, this.deleted, true, this.edited ? this._originalItem : this);
    }

    restore() {
        if (this._originalItem) {
            return this._originalItem;
        }
        return this;
    }
}


@Component({
    selector: 'formatted-data-value',
    imports: [
        PipesModule,
    ],
    template: `
        @switch (type()) {
            @case ('text') {
                <span [innerHTML]="value()"></span>
            }
            @case ('number') {
                {{ value() | value:'number' }}
            }
            @case ('number2') {
                {{ value() | value:'number2' }}
            }
            @case ('meters') {
                {{ isValueValid() ? (value() | value:'number2') + '&nbsp;m' : '' }}
            }
            @case ('currency0') {
                {{ value() | value:'price' }}
            }
            @case ('currency') {
                {{ value() | value:'price2' }}
            }
            @case ('date') {
                {{ value() | value:'date' }}
            }
            @case ('areaImperial') {
                {{ value() | areaImperial }}
            }
            @case ('areaMetric') {
                {{ value() | areaMetric }}
            }
            @case ('link') {
                @if ($any(value()).link) {
                    <a [href]="$any(value()).link" target="_blank">{{ $any(value()).name }}</a>
                }
                @else {
                    {{ $any(value()).name }}
                }
            }
        }
    `,
})
export class FormattedDataValueComponent {

    value = input();

    type = input<AsyncItemType>('text');

    isValueValid = computed(() => {
        const value = this.value();
        return typeof value !== 'undefined' && value !== null;
    });
}


@Component({
    selector: 'async-item',
    imports: [
        FormattedDataValueComponent,
    ],
    styles: [
        `
        :host {
            display: inline-flex;
            justify-content: space-between;
            align-items: center;
        }
        `,
    ],
    template: `
        @if (editing() && item().isEditable && item().state != 'new') {
            <input class="form-control" #input [value]=" item().state == 'done' ? getTextValue() : ''" (change)="edit.emit(input.value)">
            @if (item().isDeletable) {
                <button class="btn btn-transparent text-danger" title="Click to remove this field" (click)="remove.emit()">
                    <i class="fa fa-trash-o"></i>
                </button>
            }
        }
        @else {
            @switch (item().state) {
                @case ('loading') {
                    <img alt="" src="/assets/images/rolling.svg" width="24px" height="24px">
                }
                @case ('timeout') {
                    <span class="text-danger">
                        timeout
                    </span>
                }
                @case ('error') {
                    <span class="text-danger" [title]="item().value">
                        error
                    </span>
                }
                @case ('new') {
                    @if (item().isValid) {
                        <formatted-data-value [value]="item().value" [type]="item().type"></formatted-data-value>
                    }
                    @else {
                        <span class="cursor-help text-muted" title="No data is loaded yet">
                            <img alt="" src="/assets/images/cloud-arrow-down.svg" width="24px" height="24px">
                        </span>
                    }
                }
                @case ('done') {
                    @if (item().isValid) {
                        <formatted-data-value [value]="item().value" [type]="item().type"></formatted-data-value>
                        @if (editing() && item().isDeletable) {
                            <button class="btn btn-transparent text-danger" title="Click to remove this field" (click)="remove.emit()">
                                <i class="fa fa-trash-o"></i>
                            </button>
                        }
                    }
                    @else {
                        <span class="cursor-help" title="We found no data. Contact support if you think this is an error.">&ndash;</span>
                    }
                }
            }
        }
    `,
})
export class AsyncItemComponent {

    item = input.required<AsyncItem>();

    editing = input(false);

    edit = output<string>();

    remove = output();

    getTextValue() {
        const span = document.createElement('span');
        span.innerHTML = this.item().value;
        return span.textContent;
    }
}
